import { openContextModal } from '@mantine/modals';
import { construct, crush, list, pick } from 'radash';
import { snapshot } from 'valtio';
import { deepClone } from 'valtio/utils';
import { batch } from 'valtio-reactive';

import { publicConfig } from '@public-config';

import { CELL_HEIGHT, CELL_WIDTH } from '@diagram/diagram-dimensions';
import { ComponentInstanceHelpers } from '@diagram/helpers/ComponentInstanceHelpers';
import { DiagramHelpers } from '@diagram/helpers/DiagramHelpers';
import { CommunicationService } from '@diagram/services/CommunicationService';
import { ConnectionService } from '@diagram/services/ConnectionService';
import { DiagramService } from '@diagram/services/DiagramService';
import { DiagramSyncService } from '@diagram/services/DiagramSyncService';
import { LastUsedComponentInstanceService } from '@diagram/services/LastUsedComponentInstanceService';
import { PowerFlowService } from '@diagram/services/PowerFlowService';
import { SelectionService } from '@diagram/services/SelectionService';
import { ViewportService } from '@diagram/services/ViewportService';
import { WireSizeChargingService } from '@diagram/services/WireSizeChargingService';
import { ZoneService } from '@diagram/services/ZoneService';
import { copyPasteSpecificationsState } from '@diagram/state/copy-paste-specifications';
import { diagram } from '@diagram/state/diagram';
import { FaultSimulationCacheService } from '@diagram/state/fault-simulation-cache';

import {
    Component,
    ComponentInstancePortConfiguration,
    ComponentInstanceSchema,
    ComponentType,
    ComponentVisibility,
    Diagram,
    DiagramComponentInstance,
    DiagramConnection,
    DiagramConnectionAnchor,
    DiagramConnectionAnchorWithoutPort,
    DiagramPosition,
    Edge,
    getComponentPayloadValidator,
    PowerFlowDirection,
    SlackChannel,
    VoltageType,
    Zone,
} from 'models';
import { autoAssignPorts } from 'helpers/autoAssignPorts';
import { diff } from 'helpers/diff';
import { getDistanceBetweenTwoPoints } from 'helpers/dom/get-distance-between-two-points';
import { getElementCenter } from 'helpers/dom/get-element-center';
import { CheckResult } from 'helpers/PortCompatabilityChecker';
import { SyncEngineHelpers } from 'helpers/SyncEngineHelpers';

import { ApiService } from 'services/ApiService';
import { ComponentService } from 'services/ComponentService';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { LocalNotificationService } from 'services/LocalNotificationService';
import { ModalService } from 'services/ModalService';
import { TrackingService } from 'services/TrackingService';
import { state as currentProjectState } from 'state/current-project';
import { state as currentUserState } from 'state/current-user';

import { ConnectionHelpers } from '../helpers/ConnectionHelpers';
import type { Delta } from './ComponentInstanceService.type';
import { PortService } from './PortService';

type PortConfiguration = DiagramComponentInstance['configuration']['ports'][number];

const ComponentInstanceService = {
    get: (identifier?: string | null) => {
        if (!identifier) {
            return null;
        }

        const { componentInstances } = DiagramService.get();

        return (
            Object.values(componentInstances).find((componentInstance) => {
                return (
                    componentInstance.id === identifier ||
                    componentInstance.designator === identifier ||
                    componentInstance.liveDataIdentifier === identifier
                );
            }) || null
        );
    },

    getById: (id: DiagramComponentInstance['id'], diagram = DiagramService.get()): DiagramComponentInstance => {
        const componentInstance = diagram.componentInstances[id];

        if (!componentInstance) {
            throw new Error(`Component instance not found: ${id}`);
        }

        return componentInstance;
    },

    getByIdSafe: (
        id: DiagramComponentInstance['id'],
        diagram = DiagramService.get(),
    ): DiagramComponentInstance | null => {
        return diagram.componentInstances[id] || null;
    },

    getByIds: (ids: DiagramComponentInstance['id'][], diagram = DiagramService.get()) => {
        return Object.values(pick(diagram.componentInstances, ids));
    },

    getByComponentId: (componentId: DiagramComponentInstance['componentId'], diagram = DiagramService.get()) => {
        const { componentInstances } = diagram;

        return Object.values(componentInstances).filter(
            (componentInstance) => componentInstance.componentId === componentId,
        );
    },

    getByPosition: (position: DiagramPosition) => {
        return DiagramService.getComponentInstanceAtPosition(position);
    },

    getAllWithDistanceFromPoint: (position: DiagramPosition) => {
        const { componentInstances } = DiagramService.get();

        const DOMComponentInstances = document.querySelectorAll('[data-selectable-type="component-instance"]');
        const DOMComponentInstancesWithDistance = Array.from(DOMComponentInstances).map((DOMComponentInstance) => {
            const id = DOMComponentInstance.getAttribute('data-selectable-id')!;

            return {
                componentInstance: componentInstances[id],
                distance: getDistanceBetweenTwoPoints(position, getElementCenter(DOMComponentInstance)),
            };
        });

        DOMComponentInstancesWithDistance.sort((a, b) => {
            return a.distance - b.distance;
        });

        return DOMComponentInstancesWithDistance;
    },

    initialize: (
        componentType: DiagramComponentInstance['componentType'],
        componentId: DiagramComponentInstance['componentId'] = '',
        specifications?: DiagramComponentInstance['specifications'],
    ): DiagramComponentInstance => {
        const componentDefinition = ComponentInstanceHelpers.definition(componentType);

        specifications = getComponentPayloadValidator(componentType)
            // @ts-ignore pick doesn't work :(
            .pick({
                electrical: true,
            })
            .parse(specifications || {});

        const componentInstance = ComponentInstanceSchema.parse({
            id: 'new',
            position: {
                x: 0,
                y: 0,
            },
            colSpan: componentDefinition.defaultColSpan,
            rowSpan: componentDefinition.defaultRowSpan,
            componentType,
            componentId,
            indicator: 0,
            designator: '',
            specifications,
            createdAt: new Date().toISOString(),
            createdBy: currentUserState.user?.id ?? '',
        }) as DiagramComponentInstance;

        componentInstance.specifications.electrical.ports.forEach((port, index: number) => {
            const DCAvailable = 'DC' in port && port.DC.enabled;
            const ACAvailable = 'AC' in port && port.AC.enabled;

            let voltageType: VoltageType | null = DCAvailable ? 'DC' : ACAvailable ? 'AC' : null;

            if (componentDefinition.ports.defaultVoltageType === 'AC' && ACAvailable) {
                voltageType = 'AC';
            }

            if (componentDefinition.ports.defaultVoltageType === 'DC' && DCAvailable) {
                voltageType = 'DC';
            }

            componentInstance.configuration.ports[index] = ComponentInstancePortConfiguration.parse({
                label: '',
                powerFlowDirection: port.powerFlowDirection,
                voltageType,
            });
        });

        return componentInstance;
    },

    add: ({
        position,
        componentType,
        componentId = '',
        specifications,
        ...componentInstanceProps
    }: Required<Pick<DiagramComponentInstance, 'position' | 'componentType'>> &
        Partial<DiagramComponentInstance> & { skipSave?: boolean }): DiagramComponentInstance['id'] => {
        if (componentId && !specifications) {
            throw new Error('Specification must be passed with component ID.');
        }

        LastUsedComponentInstanceService.add(componentType);
        TrackingService.trackUseComponent(componentType);

        if (componentId) {
            LastUsedComponentInstanceService.addProduct(componentId);
        }

        InternalTrackingService.track('diagram.componentInstances.add', {
            position,
            componentType,
            componentId,
            specifications,
            componentInstanceProps,
        });

        const componentInstances = Object.values(diagram.componentInstances);

        const initialized = ComponentInstanceService.initialize(componentType, componentId, specifications);
        const id = componentInstanceProps.id || ComponentInstanceHelpers.generateId();
        const indicator =
            componentInstanceProps.indicator ||
            ComponentInstanceHelpers.generateIndicator(componentInstances, componentType);
        const designator =
            componentInstanceProps.designator || ComponentInstanceHelpers.generateDesignator(componentType, indicator);

        const componentInstance = {
            ...initialized,
            ...componentInstanceProps,
            id,
            indicator,
            designator,
            position,
        };

        const safeToPlace = DiagramService.safeToPlace({
            x: position.x,
            y: position.y,
            colSpan: componentInstance.colSpan,
            rowSpan: componentInstance.rowSpan,
        });

        const existing = ComponentInstanceService.getByPosition(position);

        if (!safeToPlace && existing) {
            ComponentInstanceService.replace(existing, componentInstance);

            return existing.id;
        }

        if (diagram.componentInstances[id]) {
            SyncEngineHelpers.set(diagram.componentInstances, id, componentInstance);
        } else {
            // deepClone our object before we make it a reactive proxy
            diagram.componentInstances[id] = deepClone(componentInstance);
        }

        ComponentInstanceService.splitExistingConnection(componentInstance);

        DiagramService.autoGrow();

        return id;
    },

    replace: (_existing: DiagramComponentInstance, next: DiagramComponentInstance) => {
        const existing = JSON.parse(JSON.stringify(_existing));
        const connections = ConnectionService.getConnectionsForComponentInstance(diagram, existing);

        const definition = ComponentInstanceHelpers.definition(next.componentType);

        if (connections.length > definition.ports.max) {
            ModalService.open({
                title: 'Cannot replace component',
                children: `
                    ${existing.designator} is connected to ${connections.length} other components.
                    ${definition.plural} can only connect to a maximum of ${definition.ports.max} components.
                    Please remove some connections and try again.
                `,
            });

            return;
        }

        next.position = existing.position;
        next.colSpan = existing.colSpan;
        next.rowSpan = existing.rowSpan;

        next.multiple = existing.multiple;

        next.label = existing.label;
        next.labelPlacement = existing.labelPlacement;
        next.notes = existing.notes;

        next.liveDataIdentifier = existing.liveDataIdentifier;
        next.disabled = existing.disabled;

        diagram.componentInstances[next.id] = next;

        const requiredNumberOfPorts = connections.reduce((max, connection) => {
            if (connection.from.componentInstanceId === existing.id) {
                return Math.max(max, connection.from.port || 0);
            }

            if (connection.to.componentInstanceId === existing.id) {
                return Math.max(max, connection.to.port || 0);
            }
            return max;
        }, 0);

        for (let i = 0; i <= requiredNumberOfPorts; i++) {
            if (!next.specifications.electrical.ports[i]) {
                ComponentInstanceService.addDynamicPort(next);
            }
        }

        const numberOfPorts = diagram.componentInstances[next.id].specifications.electrical.ports.length;

        connections.forEach((connection, index) => {
            for (const end of [connection.from, connection.to]) {
                if (end.componentInstanceId !== existing.id) continue;

                end.componentInstanceId = next.id;

                if (end.port !== null && end.port >= numberOfPorts) {
                    end.port = null;
                }
            }
        });

        delete diagram.componentInstances[existing.id];
    },

    copySpecifications: (id: DiagramComponentInstance['id']) => {
        copyPasteSpecificationsState.componentInstance = ComponentInstanceService.getById(id);
    },

    pasteSpecifications: (id: DiagramComponentInstance['id']) => {
        const componentInstance = ComponentInstanceService.getById(id);
        const { componentInstance: copy } = snapshot(copyPasteSpecificationsState);

        if (componentInstance && copy) {
            const specificationsWithoutPortIDs = JSON.parse(
                JSON.stringify(copy.specifications),
            ) as DiagramComponentInstance['specifications'];

            specificationsWithoutPortIDs.electrical.ports.forEach((_, index) => {
                delete specificationsWithoutPortIDs.electrical.ports[index].id;
            });

            // Charging ports carry ids too, and id based paths in nested arrays cannot be synced to the server
            if ('chargingPorts' in specificationsWithoutPortIDs.electrical) {
                specificationsWithoutPortIDs.electrical.chargingPorts.forEach((_, index) => {
                    // @ts-ignore it exists.
                    delete specificationsWithoutPortIDs.electrical.chargingPorts[index].id;
                });
            }

            DiagramSyncService.updateByKey(
                `componentInstances.${id}.specifications`,
                specificationsWithoutPortIDs,
                true,
            );
            DiagramSyncService.updateByKey(`componentInstances.${id}.configuration`, copy.configuration, true);
        }
    },

    splitExistingConnection: (componentInstance: DiagramComponentInstance) => {
        const diagram = DiagramSyncService.get();

        const id = componentInstance.id;
        const componentType = componentInstance.componentType;
        const position = componentInstance.position;

        const componentDefinition = ComponentInstanceHelpers.definition(componentType);

        // Need more than 1 port to connect to 2 components
        if (componentDefinition.ports.max < 2) return;

        // Need more than 1 free ports to connect to 2 components
        const connections = ConnectionService.getConnectionsForComponentInstance(diagram, componentInstance);
        if (componentDefinition.ports.max < connections.length) return;

        // Need to cross exactly 1 connection
        const crossingConnections = ConnectionService.getConnectionsCrossingPosition(position);
        if (crossingConnections.length !== 1) return;

        // Crossing connection should not be an existing connection
        const crossingConnection = crossingConnections[0];
        const crossingIsExistingConnection = connections.find((connection) => connection.id === crossingConnection.id);
        if (crossingIsExistingConnection) return;

        const getEdge = (positionFrom: DiagramPosition, to: DiagramConnectionAnchor) => {
            const { x: X, y: Y } = positionFrom;

            const { edge: edgeTo, componentInstanceId: componentInstanceIdTo, offset } = to;
            const { position: positionTo } = ComponentInstanceService.getById(componentInstanceIdTo, diagram) || {};

            if (!positionTo) {
                throw new Error(`No position found for component instance ${componentInstanceIdTo}`);
            }

            const x = positionTo.x + (edgeTo === 'top' || edgeTo === 'bottom' ? offset : 0);
            const y = positionTo.y + (edgeTo === 'left' || edgeTo === 'right' ? offset : 0);

            let edgeFrom: Edge = 'bottom';

            if (X === x) {
                edgeFrom = Y < y ? 'bottom' : 'top';
            } else if (Y === y) {
                edgeFrom = X < x ? 'right' : 'left';
            } else if (edgeTo === 'top' || edgeTo === 'bottom') {
                edgeFrom = X < x ? 'right' : 'left';
            } else if (edgeTo === 'left' || edgeTo === 'right') {
                edgeFrom = Y < y ? 'bottom' : 'top';
            }

            return edgeFrom;
        };

        // create extra ports for components with dynamic number of ports
        const nbExtraPorts = 2 - (componentInstance.configuration.ports.length || 0);

        list(nbExtraPorts - 1).forEach(() => {
            ComponentInstanceService.addDynamicPort(componentInstance);
        });

        // Example of how to delete a connection by manipulating the proxy:
        // delete diagram.connections[crossingConnection.id];
        ConnectionService.delete(crossingConnection.id);

        const fromComponentInstance = ComponentInstanceService.getById(crossingConnection.from.componentInstanceId);
        const toComponentInstance = ComponentInstanceService.getById(crossingConnection.to.componentInstanceId);

        const { id: _doNotUseId, to, from, ...defaultConnectionProps } = crossingConnection;
        const partialFromConnection = JSON.parse(JSON.stringify(defaultConnectionProps));
        const partialToConnection = JSON.parse(JSON.stringify(defaultConnectionProps));

        const existingVoltageType = ConnectionHelpers.getVoltageType(
            crossingConnection,
            ComponentInstanceService.getById(from.componentInstanceId),
            ComponentInstanceService.getById(to.componentInstanceId),
        );

        const length = crossingConnection.length.value || 0;
        const shortConnectionLengths: ComponentType[] = [
            'bus',
            'panel',
            'breaker',
            'contactor',
            'disconnect',
            'fuse',
            'transferSwitch',
            'precharge',
        ];

        if (length >= 2) {
            if (shortConnectionLengths.includes(componentInstance.componentType)) {
                const lengths = {
                    from: ConnectionHelpers.getPathLength({
                        fromComponentInstance,
                        fromAnchor: from,
                        toComponentInstance: componentInstance,
                        toAnchor: {
                            componentInstanceId: id,
                            edge: getEdge(position, from),
                            offset: 0,
                            port: null,
                        },
                    }),
                    to: ConnectionHelpers.getPathLength({
                        fromComponentInstance: componentInstance,
                        fromAnchor: {
                            componentInstanceId: id,
                            edge: getEdge(position, to),
                            offset: 0,
                            port: null,
                        },
                        toComponentInstance: toComponentInstance,
                        toAnchor: to,
                    }),
                };

                if (lengths.from >= lengths.to) {
                    partialToConnection.length.value = 0;
                    partialToConnection.resistance.nom = 0;
                    partialToConnection.inductance.nom = 0;
                } else {
                    partialFromConnection.length.value = 0;
                    partialFromConnection.resistance.nom = 0;
                    partialFromConnection.inductance.nom = 0;
                }
            } else if (shortConnectionLengths.includes(fromComponentInstance.componentType)) {
                partialToConnection.length.value = length;

                partialFromConnection.length.value = 0;
                partialFromConnection.resistance.nom = 0;
                partialFromConnection.inductance.nom = 0;
            } else if (shortConnectionLengths.includes(toComponentInstance.componentType)) {
                partialFromConnection.length.value = length;

                partialToConnection.length.value = 0;
                partialToConnection.resistance.nom = 0;
                partialToConnection.inductance.nom = 0;
            } else {
                const splitted = {
                    length: Math.floor(length / 2),
                    resistance: Math.floor((crossingConnection.resistance.nom || 0) / 2),
                    inductance: Math.floor((crossingConnection.inductance.nom || 0) / 2),
                };

                partialFromConnection.length.value = splitted.length;
                partialFromConnection.resistance.nom = splitted.resistance;
                partialFromConnection.inductance.nom = splitted.inductance;

                partialToConnection.length.value = splitted.length;
                partialToConnection.resistance.nom = splitted.resistance;
                partialToConnection.inductance.nom = splitted.inductance;
            }
        }

        const fromConnectionId = ConnectionService.addConnectionWithEffects({
            from: {
                ...from,
            },
            to: {
                componentInstanceId: id,
                edge: getEdge(position, from),
                offset: 0,
                port: null,
            },
            voltageTypeOverride: existingVoltageType,
            connectionProps: partialFromConnection,
        });

        const toConnectionId = ConnectionService.addConnectionWithEffects({
            from: {
                componentInstanceId: id,
                edge: getEdge(position, to),
                offset: 0,
                port: null,
            },
            to: {
                ...to,
            },
            voltageTypeOverride: existingVoltageType,
            connectionProps: partialToConnection,
        });

        const projectId = currentProjectState.project?.id;

        if (projectId) {
            const splitConnections = [
                ConnectionService.getById(fromConnectionId),
                ConnectionService.getById(toConnectionId),
            ];

            WireSizeChargingService.split(projectId, crossingConnection.id, splitConnections).catch(() => {
                console.error('Failed to carry wire sizing over to the split connections');
            });
        }
    },

    addDynamicPort: (componentInstance: DiagramComponentInstance): number | null => {
        const {
            hasDynamicNumberOfPorts,
            validators: { component: componentValidator },
            ports,
        } = ComponentInstanceHelpers.definition(componentInstance.componentType);

        if (!hasDynamicNumberOfPorts || componentInstance.componentId) {
            return null;
        }

        const numberOfPorts = componentInstance.configuration.ports.length;

        if (numberOfPorts === ports.max) {
            return null;
        }

        const existingVoltageTypes = {
            AC: 0,
            DC: 0,
        };

        componentInstance.configuration.ports.forEach((port) => {
            if (port.voltageType) {
                existingVoltageTypes[port.voltageType] += 1;
            }
        });

        const defaultPort = componentValidator.shape.electrical.parse({}).ports[0];

        diagram.componentInstances[componentInstance.id].specifications.electrical.ports.push(defaultPort);
        diagram.componentInstances[componentInstance.id].configuration.ports.push(
            ComponentInstancePortConfiguration.parse({
                voltageType: existingVoltageTypes.AC > existingVoltageTypes.DC ? 'AC' : 'DC',
            }),
        );

        return numberOfPorts;
    },

    removeDynamicPort: (componentInstance: DiagramComponentInstance, portIndex: number): void => {
        diagram.componentInstances[componentInstance.id].specifications.electrical.ports.splice(portIndex, 1);
        diagram.componentInstances[componentInstance.id].configuration.ports.splice(portIndex, 1);
    },

    switchPorts: (componentInstance: DiagramComponentInstance, source: number, target: number) => {
        const instance = diagram.componentInstances[componentInstance.id];

        const specifications = {
            source: deepClone(snapshot(instance.specifications.electrical.ports[source])),
            target: deepClone(snapshot(instance.specifications.electrical.ports[target])),
        };

        const configurations = {
            source: deepClone(snapshot(instance.configuration.ports[source])),
            target: deepClone(snapshot(instance.configuration.ports[target])),
        };

        const powerFlowDirections = {
            source: configurations.source.powerFlowDirection,
            target: configurations.target.powerFlowDirection,
        };

        SyncEngineHelpers.set(instance, `specifications.electrical.ports.${source}`, specifications.target);
        SyncEngineHelpers.set(instance, `specifications.electrical.ports.${target}`, specifications.source);

        SyncEngineHelpers.set(instance, `configuration.ports.${source}`, {
            ...configurations.target,
            powerFlowDirection: powerFlowDirections.source,
        });

        SyncEngineHelpers.set(instance, `configuration.ports.${target}`, {
            ...configurations.source,
            powerFlowDirection: powerFlowDirections.target,
        });
    },

    getConnectedPorts: (componentInstance: DiagramComponentInstance, wiring: Diagram = DiagramSyncService.get()) => {
        const connections = ConnectionService.getConnectionsForComponentInstance(wiring, componentInstance);

        const ports: number[] = [];

        connections.forEach((connection) => {
            if (connection.from.componentInstanceId === componentInstance.id && connection.from.port !== null) {
                ports.push(connection.from.port);
            }

            if (connection.to.componentInstanceId === componentInstance.id && connection.to.port !== null) {
                ports.push(connection.to.port);
            }
        });

        return ports;
    },

    getNextAvailablePort: (componentInstanceId: string) => {
        const componentInstance = ComponentInstanceService.getById(componentInstanceId);

        const usedPorts = ComponentInstanceService.getConnectedPorts(componentInstance);

        const numberOfPorts = componentInstance.configuration.ports.length;

        for (let i = 0; i < numberOfPorts; i++) {
            if (!usedPorts.includes(i)) {
                return i;
            }
        }

        return null;
    },

    update: (
        id: DiagramComponentInstance['id'],
        values: Record<string, any>,
        options = {
            effects: true,
        },
    ): boolean => {
        const componentInstance = diagram.componentInstances[id];

        if (!componentInstance) {
            // It's possible that our component instance was deleted.
            return false;
        }

        // Handle array values directly — the construct→diff pipeline uses crush()
        // which cannot represent empty arrays or detect removed array elements.
        const scalarValues: Record<string, any> = {};
        let hasArrayChanges = false;

        Object.entries(values).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                const current = SyncEngineHelpers.get(componentInstance, key);
                if (JSON.stringify(current) !== JSON.stringify(value)) {
                    SyncEngineHelpers.set(diagram.componentInstances, `${id}.${key}`, value);
                    hasArrayChanges = true;
                }
            } else {
                scalarValues[key] = value;
            }
        });

        const { hasUpdates, updates, hasDeletes } = diff(componentInstance, construct(scalarValues));

        if (!hasUpdates && !hasDeletes && !hasArrayChanges) {
            return false;
        }

        Object.entries(updates).forEach(([key, value]) => {
            SyncEngineHelpers.set(diagram.componentInstances, `${id}.${key}`, value);
        });

        if (options.effects) {
            ComponentInstanceService.effects.syncLoadCurrentToConnectionDesignCurrent(componentInstance, updates);
            ComponentInstanceService.effects.syncCircuitBreakerPoles(componentInstance, updates);
            ComponentInstanceService.effects.invalidateFaultSimulationI2t(componentInstance, updates);
        }

        return true;
    },

    effects: {
        invalidateFaultSimulationI2t: (componentInstance: DiagramComponentInstance, updates: Record<string, any>) => {
            if (componentInstance.componentType !== 'fuse') {
                return;
            }

            const changedI2t = Object.keys(updates).some((key) =>
                /^specifications\.electrical\.(meltingI2t|arcingI2t|totalClearingI2t)/.test(key),
            );

            if (changedI2t) {
                FaultSimulationCacheService.clearI2t();
            }
        },

        syncLoadCurrentToConnectionDesignCurrent: (
            componentInstance: DiagramComponentInstance,
            updates: Record<string, any>,
        ) => {
            // TODO use Svetlana's ComponentHelpers.isLoadComponent from another PR.
            const loads = ['charger', 'hvac', 'light', 'load', 'motor'];

            if (!loads.includes(componentInstance.componentType)) {
                return;
            }

            Object.entries(updates).forEach(([key, value]) => {
                const match = key.match(/specifications\.electrical\.ports\.(\d+)\.(AC|DC)\.current\.nom/);

                if (match && value && typeof value === 'number') {
                    const port = +match[1];
                    const connection = ConnectionService.getByComponentInstanceAndPort(componentInstance.id, port);

                    if (connection) {
                        const branch = ConnectionService.branch(connection);

                        branch.forEach((connection) => {
                            ConnectionService.update({
                                id: connection.id,
                                requirements: {
                                    ...connection.requirements,
                                    current: {
                                        value: value,
                                        unit: 'A',
                                    },
                                },
                            });
                        });
                    }
                }
            });
        },

        syncCircuitBreakerPoles: (componentInstance: DiagramComponentInstance, updates: Record<string, any>) => {
            if (componentInstance.componentType !== 'breaker') {
                return;
            }

            Object.entries(updates).forEach(([key, value]) => {
                const match = key.match(/specifications\.electrical\.ports\.(\d+)\.AC\.poles/);

                if (match && value) {
                    const connections = ConnectionService.getConnectionsForComponentInstance(
                        diagram,
                        componentInstance,
                    );

                    connections.forEach((connection) => {
                        const ACLines = connection.lines.AC;

                        if (value === '2P') {
                            ACLines.L1 = true;
                            ACLines.L2 = false;
                            ACLines.L3 = false;
                            ACLines.N = true;
                        }

                        if (value === '3P') {
                            ACLines.L1 = true;
                            ACLines.L2 = true;
                            ACLines.L3 = true;
                            ACLines.N = false;
                        }

                        if (value === '4P') {
                            ACLines.L1 = true;
                            ACLines.L2 = true;
                            ACLines.L3 = true;
                            ACLines.N = true;
                        }

                        ConnectionService.update({
                            id: connection.id,
                            lines: {
                                ...connection.lines,
                                AC: ACLines,
                            },
                        });
                    });
                }
            });
        },
    },

    linkProduct: ({
        componentInstance,
        productCatalogComponent,
    }: {
        componentInstance: DiagramComponentInstance;
        productCatalogComponent: Component;
    }) => {
        InternalTrackingService.track(
            'diagram.componentInstances.link',
            {
                productName: productCatalogComponent.name,
                productId: productCatalogComponent.id,
                componentType: componentInstance.componentType,
                componentInstance,
                productCatalogComponent,
            },
            { notifySlack: true, channel: SlackChannel.LOGGING },
        );

        LastUsedComponentInstanceService.addProduct(productCatalogComponent.id);

        const count =
            <T>(check: (next: T) => boolean) =>
            (count: number, next: T) =>
                check(next) ? count + 1 : count;

        const catalogComponentAcPortCount = productCatalogComponent.electrical.ports.reduce(
            count((port) => 'AC' in port && port.AC.enabled),
            0,
        );

        const catalogComponentDcPortCount = productCatalogComponent.electrical.ports.reduce(
            count((port) => 'DC' in port && port.DC.enabled),
            0,
        );

        let diagram = DiagramSyncService.get();
        const configurationPortsToCheck = componentInstance.configuration.ports.filter((_port, index) =>
            PortService.isConnected({
                componentInstanceId: componentInstance.id,
                portKey: index,
                connections: Object.values(diagram.connections),
            }),
        );

        const componentInstanceAcPortCount = configurationPortsToCheck.reduce(
            count((port) => port.voltageType === 'AC'),
            0,
        );

        const componentInstanceDcPortCount = configurationPortsToCheck.reduce(
            count((port) => port.voltageType === 'DC'),
            0,
        );

        if (componentInstanceAcPortCount > catalogComponentAcPortCount) {
            throw new Error('Not enough AC ports!');
        }

        if (componentInstanceDcPortCount > catalogComponentDcPortCount) {
            throw new Error('Not enough DC ports!');
        }

        const autoAssignResult = autoAssignPorts({ componentInstance, productCatalogComponent });
        const portMapping = new Map(
            autoAssignResult.map((mapping) => [mapping.componentInstancePort, mapping.productCatalogComponentPort]),
        );

        const updatedConfigurationPorts: DiagramComponentInstance['configuration']['ports'] = [];

        autoAssignResult.forEach((portResult) => {
            updatedConfigurationPorts[portResult.productCatalogComponentPort] =
                ComponentInstanceService.getPortConfigurationFromCheckResultAndConfiguration(
                    portResult.checkResult,
                    componentInstance.configuration.ports[portResult.componentInstancePort],
                );
        });

        // Handle ports that were not auto-assigned
        for (
            let productCatalogComponentPortIndex = 0;
            productCatalogComponentPortIndex < productCatalogComponent.electrical.ports.length;
            productCatalogComponentPortIndex++
        ) {
            const isMapped = [...portMapping.values()].includes(productCatalogComponentPortIndex);

            if (isMapped) {
                continue;
            }

            const portConfiguration =
                ComponentInstanceService.getPortConfigurationFromProductCatalogProductSpecification(
                    productCatalogComponent.electrical.ports[productCatalogComponentPortIndex],
                );

            // Insert in first empty array element or push to the end
            const emptyIndex = updatedConfigurationPorts.findIndex((port) => !port);
            if (emptyIndex !== -1) {
                updatedConfigurationPorts[emptyIndex] = portConfiguration;
            } else {
                updatedConfigurationPorts.push(portConfiguration);
            }
        }

        diagram = DiagramSyncService.get();

        diagram.componentInstances[componentInstance.id].componentId = productCatalogComponent.id;

        /* Remove port ID's from productCatalogComponent */
        productCatalogComponent.electrical.ports.forEach((_, index) => {
            delete productCatalogComponent.electrical.ports[index].id;
        });

        if ('chargingPorts' in productCatalogComponent.electrical) {
            productCatalogComponent.electrical.chargingPorts.forEach((_, index) => {
                // @ts-ignore it exists.
                delete productCatalogComponent.electrical.chargingPorts[index].id;
            });
        }

        SyncEngineHelpers.set(
            diagram.componentInstances[componentInstance.id],
            'specifications.electrical',
            productCatalogComponent.electrical,
        );

        SyncEngineHelpers.set(
            diagram.componentInstances[componentInstance.id],
            'configuration.ports',
            updatedConfigurationPorts,
        );

        // In case a solution gets linked to a battery, update the component type
        diagram.componentInstances[componentInstance.id].componentType = productCatalogComponent.type;

        for (const [connectionId, connection] of Object.entries(diagram.connections)) {
            for (const anchorType of ['to', 'from'] as const) {
                const anchor = connection[anchorType];

                if (
                    anchor.componentInstanceId === componentInstance.id &&
                    anchor.port !== null &&
                    portMapping.has(anchor.port)
                ) {
                    diagram.connections[connectionId][anchorType].port = portMapping.get(anchor.port) ?? null;
                }
            }
        }

        // The product limits which directions its ports can take, so each connected port takes the one its wiring
        // can follow with the fewest changes. This has to run after linking: the plan reads the product's limits.
        ComponentInstanceService.getConnectedPorts(componentInstance).forEach((port) => {
            const start = { componentInstanceId: componentInstance.id, port };
            const plan = PowerFlowService.getBestPowerFlowPlan(start, ['bidirectional', 'input', 'output']);

            if (plan) {
                PowerFlowService.applyPowerFlowPlan(plan);

                return;
            }

            // No direction fits the wiring, so the port keeps the one it was matched with and the user is told why
            // that one doesn't fit.
            const matched =
                diagram.componentInstances[componentInstance.id].configuration.ports[port]?.powerFlowDirection;
            const matchedPlan = matched && PowerFlowService.getPowerFlowPlan(start, matched);

            if (matchedPlan && !matchedPlan.possible) {
                LocalNotificationService.showInfo({ message: matchedPlan.reason });
            }
        });
    },

    unlinkProduct: (componentInstanceId: string) => {
        DiagramSyncService.updateByKey(`componentInstances.${componentInstanceId}.componentId`, '', true);

        const componentInstance = ComponentInstanceService.getById(componentInstanceId);
        const electrical = componentInstance.specifications.electrical;

        InternalTrackingService.track('diagram.componentInstances.unlink', {
            componentInstance,
        });

        componentInstance.specifications.electrical.ports.forEach((_, index) => {
            if ('id' in electrical.ports[index]) {
                DiagramSyncService.delete(
                    `componentInstances.${componentInstanceId}.specifications.electrical.ports.${index}.id`,
                    true,
                );
            }

            if ('AC' in electrical.ports[index]) {
                DiagramSyncService.updateByKey(
                    `componentInstances.${componentInstanceId}.specifications.electrical.ports.${index}.AC.enabled`,
                    true,
                    true,
                );
            }

            if ('DC' in electrical.ports[index]) {
                DiagramSyncService.updateByKey(
                    `componentInstances.${componentInstanceId}.specifications.electrical.ports.${index}.DC.enabled`,
                    true,
                    true,
                );
            }

            if ('purpose' in electrical.ports[index]) {
                DiagramSyncService.delete(
                    `componentInstances.${componentInstanceId}.specifications.electrical.ports.${index}.purpose`,
                    true,
                );
            }
        });
    },

    getPortConfigurationFromCheckResultAndConfiguration: (
        { powerFlowDirectionMatch, voltageTypeMatch }: CheckResult,
        configuration = {} as Partial<PortConfiguration>,
    ): DiagramComponentInstance['configuration']['ports'][number] => {
        if (!powerFlowDirectionMatch) {
            throw new Error('Expected to find power flow direction match');
        }

        if (!voltageTypeMatch) {
            throw new Error('Expected to find voltage type match');
        }

        return {
            ...ComponentInstanceService.getDefaultPortConfiguration(),
            ...configuration,
            powerFlowDirection: powerFlowDirectionMatch,
            voltageType: voltageTypeMatch,
        };
    },

    getPortConfigurationFromProductCatalogProductSpecification: (
        specification: Component['electrical']['ports'][number],
    ): DiagramComponentInstance['configuration']['ports'][number] => {
        const dcEnabled = 'DC' in specification && specification.DC.enabled;
        const acEnabled = 'AC' in specification && specification.AC.enabled;

        const voltageType = dcEnabled ? 'DC' : acEnabled ? 'AC' : null;

        return {
            ...ComponentInstanceService.getDefaultPortConfiguration(),
            powerFlowDirection: specification.powerFlowDirection,
            voltageType,
        };
    },

    getDefaultPortConfiguration: () => {
        return ComponentInstancePortConfiguration.parse({});
    },

    move: (
        idOrArray: DiagramComponentInstance['id'] | DiagramComponentInstance['id'][],
        delta: DiagramPosition,
    ): void => {
        if (typeof idOrArray === 'string') {
            return ComponentInstanceService.move([idOrArray], delta);
        }

        const diagram = DiagramSyncService.get();

        const componentInstances = ComponentInstanceService.getByIds(idOrArray);

        if (DiagramService.safeToMove(componentInstances, delta)) {
            componentInstances.forEach((componentInstance) => {
                componentInstance.position.x += delta.x;
                componentInstance.position.y += delta.y;

                ComponentInstanceService.splitExistingConnection(componentInstance);
            });

            ComponentInstanceService.updateAnchorsForSelection(componentInstances, diagram);

            DiagramService.autoGrow();
        }
    },

    updateAnchorsForSelection: (componentInstances: DiagramComponentInstance[], diagram: Diagram) => {
        const allConnections: DiagramConnection[] = [];

        componentInstances.forEach((componentInstance) => {
            const componentConnections = ConnectionService.getConnectionsForComponentInstance(
                diagram,
                componentInstance,
            );

            allConnections.push(...componentConnections);
        });

        allConnections.forEach((connection) => {
            ConnectionService.updateAnchors(connection);
        });
    },

    resize: (componentInstance: DiagramComponentInstance, delta: Delta) => {
        const { x, y } = delta;

        const newPositionAndSize = ComponentInstanceService.getNewPositionAndSize(componentInstance, delta);

        ComponentInstanceService.update(componentInstance.id, {
            ...newPositionAndSize,
        });

        const anchors = ComponentInstanceService.getConnectedAnchors(componentInstance);

        anchors.forEach(({ connectionId, endpoint, edge, offset }) => {
            const newOffset = offset - (edge === 'top' || edge === 'bottom' ? x : y);

            ConnectionService.setAnchorOffset(connectionId, endpoint, newOffset);
        });
    },

    delete: (idOrArray: DiagramComponentInstance['id'] | DiagramComponentInstance['id'][]): void => {
        if (typeof idOrArray === 'string') {
            return ComponentInstanceService.delete([idOrArray]);
        }

        const diagram = DiagramSyncService.get();

        idOrArray.forEach((componentInstanceId) => {
            const componentInstance = ComponentInstanceService.getByIdSafe(componentInstanceId, diagram);

            InternalTrackingService.track('diagram.componentInstance.delete', {
                componentInstance,
            });

            if (componentInstance) {
                delete diagram.componentInstances[componentInstanceId];

                const connections = ConnectionService.getConnectionsForComponentInstance(diagram, componentInstance);
                const communications = CommunicationService.getForComponentInstance(componentInstance.id);

                if (connections.length === 2 && !ComponentInstanceHelpers.actsAsConverter(componentInstance)) {
                    ConnectionService.combine(connections[0], connections[1]);
                }

                connections.forEach((connection) => {
                    delete diagram.connections[connection.id];
                });

                communications.forEach((communication) => {
                    delete diagram.communications[communication.id];
                });
            }
        });
    },

    getConnections: (componentInstance: DiagramComponentInstance) => {
        const { connections } = DiagramService.get();

        return Object.values(connections).filter(
            (connection) =>
                connection.from.componentInstanceId === componentInstance.id ||
                connection.to.componentInstanceId === componentInstance.id,
        );
    },

    getAnchors: (componentInstance: DiagramComponentInstance, withPorts = false) => {
        const { connections } = DiagramSyncService.get();
        const anchors: DiagramConnectionAnchor[] = [];

        const { id, colSpan, rowSpan } = componentInstance;
        const definition = ComponentInstanceHelpers.definition(componentInstance.componentType);

        if (definition.ports.max === 0) {
            return [];
        }

        const getPort = withPorts
            ? (anchor: DiagramConnectionAnchorWithoutPort) => {
                  for (const connection of Object.values(connections)) {
                      if (ComponentInstanceService.isMatchingAnchor(anchor, connection.to)) {
                          return connection.to.port;
                      }

                      if (ComponentInstanceService.isMatchingAnchor(anchor, connection.from)) {
                          return connection.from.port;
                      }
                  }

                  return null;
              }
            : () => null;

        list(colSpan - 1).forEach((offset) => {
            const edges: DiagramConnectionAnchor['edge'][] = ['top', 'bottom'];

            edges.forEach((edge) => {
                const anchorWithoutPort = {
                    componentInstanceId: componentInstance.id,
                    edge,
                    offset,
                };

                anchors.push({ ...anchorWithoutPort, port: getPort(anchorWithoutPort) });
            });
        });

        list(rowSpan - 1).forEach((offset) => {
            const edges: DiagramConnectionAnchor['edge'][] = ['left', 'right'];

            edges.forEach((edge) => {
                const anchorWithoutPort = {
                    componentInstanceId: id,
                    edge,
                    offset,
                };

                anchors.push({ ...anchorWithoutPort, port: getPort(anchorWithoutPort) });
            });
        });

        return anchors;
    },

    getConnectedAnchors: (
        componentInstance: DiagramComponentInstance,
    ): (DiagramConnectionAnchor & { connectionId: DiagramConnection['id']; endpoint: 'to' | 'from' })[] => {
        const connections = ComponentInstanceService.getConnections(componentInstance);

        return connections.map((connection) => {
            const anchor =
                connection.from.componentInstanceId === componentInstance.id ? connection.from : connection.to;

            const endpoint = connection.from.componentInstanceId === componentInstance.id ? 'from' : 'to';

            return { ...anchor, connectionId: connection.id, endpoint };
        });
    },

    isMatchingAnchor: (
        nonPortAnchor: DiagramConnectionAnchorWithoutPort,
        diagramAnchor: DiagramConnectionAnchor,
    ): boolean => {
        return (
            nonPortAnchor.componentInstanceId === diagramAnchor.componentInstanceId &&
            nonPortAnchor.edge === diagramAnchor.edge &&
            nonPortAnchor.offset === diagramAnchor.offset
        );
    },

    openCreateReusableComponentModal: (componentInstance: DiagramComponentInstance) => {
        openContextModal({
            modal: 'createReusableComponent',
            innerProps: {
                componentInstance,
            },
            withCloseButton: false,
        });
    },

    createGenericReusableComponent: async (componentInstance: DiagramComponentInstance, name: string = '') => {
        const component = ComponentInstanceService.convertComponentInstanceSpecToComponent(componentInstance, name);

        const createdComponent = await ComponentService.create(component);

        if (!createdComponent) {
            throw new Error('Could not create new component');
        }

        ComponentInstanceService.linkProduct({ componentInstance, productCatalogComponent: createdComponent });
    },

    convertComponentInstanceSpecToComponent: (componentInstance: DiagramComponentInstance, name?: string) => {
        const componentValidator = getComponentPayloadValidator(componentInstance.componentType);

        return componentValidator.parse({
            name: name || componentInstance.label || componentInstance.designator,
            teamManufacturer: componentInstance.manufacturer ?? '',
            productIdentifier: componentInstance.partNumber ?? '',
            electrical: componentInstance.specifications.electrical,
            visibility: ComponentVisibility.PRIVATE,
        });
    },

    moveIntoView: (idOrArray: string | string[], highlight = false) => {
        if (typeof idOrArray === 'string') {
            const { position, colSpan, rowSpan } = ComponentInstanceService.getById(idOrArray);

            ViewportService.moveIntoView({
                x: position.x * CELL_WIDTH + (colSpan * CELL_WIDTH) / 2,
                y: position.y * CELL_HEIGHT + (rowSpan * CELL_HEIGHT) / 2,
            });

            if (highlight) {
                ComponentInstanceService.highlight(idOrArray);
            }

            return;
        }

        const componentInstances = ComponentInstanceService.getByIds(idOrArray);

        const zone: Zone = {
            id: 'temp',
            type: 'control',
            connections: [],
            componentInstances: componentInstances,
        };

        if (highlight) {
            ZoneService.highlight(zone);
        }

        ZoneService.moveIntoView(zone);
    },

    highlight: (id: string, level: 'notice' | 'error' | 'success' = 'notice') => {
        const componentInstance = document.querySelector(`[data-selectable-id=${id}]`);

        if (componentInstance) {
            const animation = () => {
                componentInstance.removeAttribute('data-highlighted');
                componentInstance.removeEventListener('animationend', animation);
            };

            componentInstance.addEventListener('animationend', animation);
            componentInstance.setAttribute('data-highlighted', level);
        }
    },

    getNewPositionAndSize: (
        componentInstance: DiagramComponentInstance,
        delta: Delta,
    ): {
        position: DiagramComponentInstance['position'];
        colSpan: number;
        rowSpan: number;
    } => ({
        position: {
            x: componentInstance.position.x + delta.x,
            y: componentInstance.position.y + delta.y,
        },
        colSpan: componentInstance.colSpan + delta.width,
        rowSpan: componentInstance.rowSpan + delta.height,
    }),

    validToResize: (componentInstance: DiagramComponentInstance, delta: Delta): boolean => {
        const newPositionAndSize = ComponentInstanceService.getNewPositionAndSize(componentInstance, delta);

        const {
            position: { x, y },
            colSpan,
            rowSpan,
        } = newPositionAndSize;

        // Check if it does not undercut the minimum size
        // Component should minimally span [x1:x2 x y1:y2] to include all anchors
        const anchors = ComponentInstanceService.getConnectedAnchors(componentInstance);

        let x1 = Infinity;
        let x2 = -Infinity;
        let y1 = Infinity;
        let y2 = -Infinity;

        anchors.forEach((anchor) => {
            if (anchor.edge === 'top' || anchor.edge === 'bottom') {
                // dx to account for the fact that the anchors will move after the resize
                const anchorX = anchor.offset + x - delta.x;

                x1 = Math.min(x1, anchorX);
                x2 = Math.max(x2, anchorX);
            }

            if (anchor.edge === 'left' || anchor.edge === 'right') {
                // dy to account for the fact that the anchors will move after the resize
                const anchorY = anchor.offset + y - delta.y;

                y1 = Math.min(y1, anchorY);
                y2 = Math.max(y2, anchorY);
            }
        });

        const allowResize = x <= x1 && x + colSpan > x2 && y <= y1 && y + rowSpan > y2;

        if (!allowResize) return false;

        // Check if it is safe to place the component at the new position

        const safeToPlace = DiagramService.safeToPlace(
            {
                x: newPositionAndSize.position.x,
                y: newPositionAndSize.position.y,
                colSpan: newPositionAndSize.colSpan,
                rowSpan: newPositionAndSize.rowSpan,
            },
            componentInstance.id,
        );

        return allowResize && safeToPlace;
    },

    setDisabled: (disabled: boolean) => {
        const selectedComponentInstances = SelectionService.getSelectedComponentInstances();

        const componentInstances = ComponentInstanceService.getByIds(selectedComponentInstances);

        batch(() => {
            componentInstances.forEach((componentInstance) => {
                SyncEngineHelpers.set(diagram.componentInstances, `${componentInstance.id}.disabled`, disabled);
            });
        });

        DiagramSyncService.save();
    },

    setDisabledExceptSelected: () => {
        const selectedComponentInstanceIds = SelectionService.getSelectedComponentInstances();

        const allComponentInstanceIds = Object.keys(diagram.componentInstances);

        batch(() => {
            allComponentInstanceIds.forEach((id) => {
                if (!selectedComponentInstanceIds.includes(id)) {
                    SyncEngineHelpers.set(diagram.componentInstances, `${id}.disabled`, true);
                }
            });
        });

        DiagramSyncService.save();
    },

    fillFromReference: (mutableComponentInstance: DiagramComponentInstance, _reference: string) => {
        const componentInstance = mutableComponentInstance;
        const reference = currentProjectState.diagrams.find((diagram) => {
            return diagram.id === _reference;
        });

        if (reference) {
            const connectors = DiagramHelpers.connectors(reference);

            componentInstance.specifications.reference = reference.id;
            componentInstance.specifications.electrical.ports = connectors;

            for (let i = 0; i < connectors.length; i++) {
                const port = componentInstance.configuration.ports[i];

                if (!port) {
                    componentInstance.configuration.ports[i] = ComponentInstanceService.getDefaultPortConfiguration();
                    componentInstance.configuration.ports[i].voltageType = 'DC' in connectors[i] ? 'DC' : 'AC';
                }
            }

            componentInstance.configuration.ports = componentInstance.configuration.ports.slice(0, connectors.length);
        }
    },

    flipOrientation: (componentInstance: DiagramComponentInstance) => {
        const [first, second, ...rest] = ConnectionService.getConnectionsForComponentInstance(
            diagram,
            componentInstance,
        );

        if (rest.length) {
            // Return early if there would be more than 2 connections
            return;
        }

        let firstEndpoint: 'from' | 'to' | null = null;
        let secondEndpoint: 'from' | 'to' | null = null;

        if (first && first.from.componentInstanceId === componentInstance.id) {
            firstEndpoint = 'from';
        }

        if (first && first.to.componentInstanceId === componentInstance.id) {
            firstEndpoint = 'to';
        }

        if (second && second.from.componentInstanceId === componentInstance.id) {
            secondEndpoint = 'from';
        }

        if (second && second.to.componentInstanceId === componentInstance.id) {
            secondEndpoint = 'to';
        }

        if (!firstEndpoint || !secondEndpoint) {
            return;
        }

        const firstPort = first[firstEndpoint].port;
        const secondPort = second[secondEndpoint].port;

        const swapPorts = () => {
            diagram.connections[first.id][firstEndpoint].port = secondPort;
            diagram.connections[second.id][secondEndpoint].port = firstPort;
        };

        if (firstPort === null || secondPort === null) {
            swapPorts();

            return;
        }

        const flipped: Diagram = {
            ...diagram,
            connections: {
                ...diagram.connections,
                [first.id]: { ...first, [firstEndpoint]: { ...first[firstEndpoint], port: secondPort } },
                [second.id]: { ...second, [secondEndpoint]: { ...second[secondEndpoint], port: firstPort } },
            },
        };

        const { ports } = componentInstance.configuration;

        // Keep the wires flowing as before if the device allows it, otherwise reverse the flow through it.
        const candidates = [ports[secondPort]?.powerFlowDirection, ports[firstPort]?.powerFlowDirection].filter(
            (powerFlowDirection): powerFlowDirection is PowerFlowDirection => !!powerFlowDirection,
        );

        const plans = candidates.map((powerFlowDirection) =>
            PowerFlowService.getPowerFlowPlan(
                { componentInstanceId: componentInstance.id, port: firstPort },
                powerFlowDirection,
                { wiring: flipped },
            ),
        );

        const plan = plans.find((plan) => plan.possible) ?? plans.at(-1);

        if (plan && !plan.possible) {
            PowerFlowService.applyPowerFlowPlan(plan);

            return;
        }

        swapPorts();

        if (plan) {
            PowerFlowService.applyPowerFlowPlan(plan);
        }
    },

    charge: async () => {
        const projectId = currentProjectState.project?.id;

        if (!projectId) {
            return;
        }

        // Disable for now..
        // await ApiService.post(`${publicConfig.urls.api}/projects/${projectId}/charge-component-instance-placement`);
    },
};

export { ComponentInstanceService };
